import React from 'react';
import { Button, Card, Grid } from 'ui-toolkit';
import { v4 } from 'uuid';
import { IWhoHasFocus } from './IWhoHasFocus';
import { PartyInformationForm } from './PartyInformationForm';
import { NewOrderSpaceWrapping } from '../wrappers/NewOrderSpaceWrapper';
import { Field, FieldArray } from 'formik';
import { IPartySchema } from '../validation/PartySchema';
import { DisplayPartyPurpose, ePartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';
import { ePartyType } from '../../../ui-toolkit/enums/ePartyType';
import { mergeTests } from 'ui-toolkit/test-utilities/mergeTests';
import type { FCWithChildren } from '../../../types/FCWithChildren';

interface IPartiesGroupInformationFormProps {
  title: string;
  minimum: number;
  maximum: number;
  partyPurpose: ePartyPurpose;
  focus: IWhoHasFocus;
  setFocus: (x: IWhoHasFocus) => void;
}

const AddButtonRow: FCWithChildren = ({ children }) => (
  <div className={'flex flex-row items-center'}>
    <div className={'basis-2/5 border-t border-neutral-200'} />
    <div className={'grid grow'}>{children}</div>
    <div className={'basis-2/5 border-t border-neutral-200'} />
  </div>
);

export const PartiesGroupInformationForm: React.FC<IPartiesGroupInformationFormProps> = ({
  title,
  minimum,
  maximum,
  partyPurpose,
  focus,
  setFocus,
}) => {
  const addParty = (push: (x: any) => void, length: number) => {
    return () => {
      push({
        id: v4(),
        type: ePartyType.person,
        purpose: partyPurpose,
        firstName: null,
        middleName: null,
        lastName: null,
        email: null,
        ssn: null,
        maritalStatus: '',
        phoneNumber: null,
        entityName: null,
        entityType: null,
        dateOfBirth: null,
        address: null,
        signatories: [],
      });
      setFocus({ doesAnyone: true, partyIndex: length, signatoryIndex: -1 });
    };
  };
  return (
    <NewOrderSpaceWrapping>
      <Card title={title} data-test-id={mergeTests('parties-card', partyPurpose)}>
        <Field name="parties">
          {({ field }: any) => (
            <FieldArray name="parties">
              {({ push, remove }) => {
                const parties = field.value;
                const thisGroup = parties.filter((t: IPartySchema) => t.purpose === partyPurpose);
                const thisGroupCount = thisGroup.length;
                return (
                  <Grid>
                    {parties.map(
                      (t: IPartySchema, i: number) =>
                        t.purpose === partyPurpose && (
                          <PartyInformationForm
                            key={t.id}
                            title={title}
                            index={i}
                            displayIndex={thisGroup.indexOf(t)}
                            onClickDelete={() => remove(i)}
                            showDelete={thisGroupCount > minimum}
                            focus={focus}
                            setFocus={setFocus}
                          />
                        )
                    )}
                    {thisGroupCount < maximum && (
                      <NewOrderSpaceWrapping>
                        <AddButtonRow>
                          <Button
                            intent="secondary"
                            type={'button'}
                            onClick={addParty(push, parties.length)}
                            data-test-id={mergeTests('parties', `add`, partyPurpose)}
                          >
                            + Add {DisplayPartyPurpose[partyPurpose]}
                          </Button>
                        </AddButtonRow>
                      </NewOrderSpaceWrapping>
                    )}
                  </Grid>
                );
              }}
            </FieldArray>
          )}
        </Field>
      </Card>
    </NewOrderSpaceWrapping>
  );
};
